import React from 'react';
import { Table, Form, Pagination } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

const MOEDA_LABEL = { R: 'Real', D: 'Dólar', E: 'Euro', L: 'Libra', P: 'Peso' };

const GridDespesasElegiveis = ({
  despesas,
  selecionados,
  onToggleSelecionado,
  onToggleTodos,
  ordenacao,
  onOrdenar,
  pagina = 1,
  itensPorPagina = 50,
  onChangePagina,
  valoresIofPorDespesa = {},
  onChangeValoresIof,
}) => {
  const temEstrangeiraNaLista = despesas.some((d) => d.moeda && d.moeda !== 'R');
  const totalPaginas = Math.max(1, Math.ceil(despesas.length / itensPorPagina));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const inicio = (paginaAtual - 1) * itensPorPagina;
  const fim = inicio + itensPorPagina;
  const despesasPagina = despesas.slice(inicio, fim);

  const codigosPagina = despesasPagina.map((d) => d.codigo);
  const todosDaPaginaMarcados =
    despesasPagina.length > 0 && codigosPagina.every((c) => selecionados.includes(c));

  const iconeOrdenacao = (campo) => {
    if (ordenacao.campo !== campo) return <FaSort style={{ opacity: 0.3, marginLeft: 3 }} />;
    return ordenacao.direcao === 'asc'
      ? <FaSortUp style={{ marginLeft: 3 }} />
      : <FaSortDown style={{ marginLeft: 3 }} />;
  };

  const thSort = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

  const formatarData = (val) => {
    if (!val) return '-';
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return val;
    return d.toLocaleDateString('pt-BR');
  };

  const formatarValor = (v) => {
    if (v == null) return '-';
    return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  if (despesas.length === 0) {
    return <p className="text-muted text-center my-3">Nenhuma despesa elegível encontrada.</p>;
  }

  const renderizarPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const maxBotoes = 5;
    const items = [];
    items.push(
      <Pagination.First key="first" onClick={() => onChangePagina && onChangePagina(1)} disabled={paginaAtual === 1} />
    );
    items.push(
      <Pagination.Prev key="prev" onClick={() => onChangePagina && onChangePagina(paginaAtual - 1)} disabled={paginaAtual === 1} />
    );
    let ini = Math.max(1, paginaAtual - Math.floor(maxBotoes / 2));
    let limite = Math.min(totalPaginas, ini + maxBotoes - 1);
    if (limite - ini < maxBotoes - 1) ini = Math.max(1, limite - maxBotoes + 1);
    if (ini > 1) items.push(<Pagination.Ellipsis key="e1" disabled />);
    for (let i = ini; i <= limite; i += 1) {
      const n = i;
      items.push(
        <Pagination.Item key={n} active={n === paginaAtual} onClick={() => onChangePagina && onChangePagina(n)}>
          {n}
        </Pagination.Item>
      );
    }
    if (limite < totalPaginas) items.push(<Pagination.Ellipsis key="e2" disabled />);
    items.push(
      <Pagination.Next key="next" onClick={() => onChangePagina && onChangePagina(paginaAtual + 1)} disabled={paginaAtual === totalPaginas} />
    );
    items.push(
      <Pagination.Last key="last" onClick={() => onChangePagina && onChangePagina(totalPaginas)} disabled={paginaAtual === totalPaginas} />
    );
    return (
      <div className="d-flex justify-content-between align-items-center px-2 py-2" style={{ borderTop: '1px solid #dee2e6' }}>
        <small className="text-muted">
          Mostrando {inicio + 1} até {Math.min(fim, despesas.length)} de {despesas.length} registros
        </small>
        <Pagination className="mb-0">{items}</Pagination>
      </div>
    );
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table striped bordered hover className="mb-0">
        <thead>
          <tr>
            <th style={{ width: 40, textAlign: 'center' }}>
              <Form.Check
                type="checkbox"
                checked={todosDaPaginaMarcados}
                onChange={() => onToggleTodos(codigosPagina, todosDaPaginaMarcados)}
                id="sel-todos"
                title="Selecionar todos da página"
              />
            </th>
            <th style={thSort} onClick={() => onOrdenar('numero')}>
              Nº {iconeOrdenacao('numero')}
            </th>
            <th style={thSort} onClick={() => onOrdenar('data_compra')}>
              Data Compra {iconeOrdenacao('data_compra')}
            </th>
            <th style={thSort} onClick={() => onOrdenar('vencimento')}>
              Vencimento {iconeOrdenacao('vencimento')}
            </th>
            <th style={thSort} onClick={() => onOrdenar('nome_fornecedor')}>
              Fornecedor {iconeOrdenacao('nome_fornecedor')}
            </th>
            <th style={{ ...thSort, textAlign: 'right' }} onClick={() => onOrdenar('valor')}>
              Valor {iconeOrdenacao('valor')}
            </th>
            <th style={thSort} onClick={() => onOrdenar('moeda')}>
              Moeda {iconeOrdenacao('moeda')}
            </th>
            {temEstrangeiraNaLista && (
              <>
                <th style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>Cotação *</th>
                <th style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>IOF *</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {despesasPagina.map((d) => {
            const marcado = selecionados.includes(d.codigo);
            return (
              <tr
                key={`elegivel-${d.codigo}`}
                style={{ cursor: 'pointer', backgroundColor: marcado ? '#e8f4f8' : undefined }}
                onClick={() => onToggleSelecionado(d.codigo)}
              >
                <td className="text-center" onClick={(e) => e.stopPropagation()}>
                  <Form.Check
                    type="checkbox"
                    checked={marcado}
                    onChange={() => onToggleSelecionado(d.codigo)}
                    id={`sel-${d.codigo}`}
                  />
                </td>
                <td>{d.numero || '-'}</td>
                <td>{formatarData(d.data_compra)}</td>
                <td>{formatarData(d.vencimento)}</td>
                <td>{d.nome_fornecedor || '-'}</td>
                <td className="text-right">{formatarValor(d.valor)}</td>
                <td>{MOEDA_LABEL[d.moeda] || d.moeda || '-'}</td>
                {temEstrangeiraNaLista && (
                  <>
                    <td
                      className="text-right"
                      style={{ minWidth: 110 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {d.moeda && d.moeda !== 'R' ? (
                        <Form.Control
                          type="number"
                          step="0.000001"
                          min="0"
                          size="sm"
                          value={(valoresIofPorDespesa[d.codigo] && valoresIofPorDespesa[d.codigo].cotacao) || ''}
                          onChange={(e) =>
                            onChangeValoresIof && onChangeValoresIof(d.codigo, 'cotacao', e.target.value)
                          }
                          style={{ textAlign: 'right' }}
                        />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td
                      className="text-right"
                      style={{ minWidth: 110 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {d.moeda && d.moeda !== 'R' ? (
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          size="sm"
                          value={(valoresIofPorDespesa[d.codigo] && valoresIofPorDespesa[d.codigo].iof) || ''}
                          onChange={(e) =>
                            onChangeValoresIof && onChangeValoresIof(d.codigo, 'iof', e.target.value)
                          }
                          style={{ textAlign: 'right' }}
                        />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>
      {renderizarPaginacao()}
    </div>
  );
};

export default GridDespesasElegiveis;
